import { useEffect, useState, useCallback, useRef } from 'react';
import h, { getCountry } from '../../helpers/common';
import playerService from '../../helpers/bitmovinPlayer';
import apis from '../../helpers/apis';
import to from 'await-to-js';
import { useDispatch } from 'react-redux';
import user from '../../helpers/user';
import { GamificationBookmarkUtils } from '@intigral/utils/lib/gamification/GamificationBookmarkUtils';
import { PlayerEvent } from 'bitmovin-player';
import { isPlayerAlive, safePlayerOff } from '../../helpers/playerGuards';
import { screenDetailTypes } from '@libs/constants';
import { useConfigurationSections } from '../useConfiguration';
import { useGetBookMarksQuery } from '@intigral/domain/lib/presentation/hooks/useGetBookMarksQuery';
import { useAddBookmarkMutation } from './useAddBookmark';
import { useDeleteBookmarkMutation } from './useDeleteBookmark';

interface Bookmark {
    listingId: string | null;
    paId: string;
    mediaId: string | null;
    programType: string | null;
}

const useBookmark = (profileData?: any) => {
    const [selected, setSelected] = useState<Bookmark | undefined>();
    const { data: bookmarks, isLoading } = useGetBookMarksQuery({
        apiKey: user.getAPIKey(),
        profileGuid: user.getProfile()?.profileGuid,
        authToken: user.getUserToken(),
        identityToken: user.getIdentityToken(),
        country: getCountry(),
        device: h.getDevice(),
        programType: 'all',
    });
    const dispatch = useDispatch();
    const { config } = useConfigurationSections();
    const bookmarksInterval = config?.global?.playback?.bookmarkInterval ?? 10;
    const addBookmarkMutation = useAddBookmarkMutation();
    const addBookmark = useCallback(
        (content: Bookmark, timeCode: number, duration: number) => {
            const isNotLinear = [
                screenDetailTypes.EPISODE,
                screenDetailTypes.MOVIES,
                screenDetailTypes.SERIES,
            ].includes(content.programType)
                ? content.mediaId
                : content.listingId;
            // Use React Query configuration instead of localStorage
            const segmentSize = config?.gamificationConfigs?.assetSegmentSizeInSeconds || 120;
            const configBookmarksInterval = config?.global?.playback?.bookmarkInterval ?? 10;
            // Only send bookmark at segment boundaries
            // if (!GamificationBookmarkUtils.shouldSendBookmark(timeCode, bookmarksInterval)) return;
            // Use single utility method for gamification fields
            const gamificationFields = GamificationBookmarkUtils.generateGamificationFields(
                timeCode,
                isNotLinear ? content.mediaId : content.listingId,
                content.paId || '',
                segmentSize
            );
            // Return the mutation promise so callers (e.g. player close) can await
            // before refreshing Continue Watching detailed-bookmarks.
            // Catch so fire-and-forget callers (interval/pause) don't get unhandled rejections.
            return addBookmarkMutation
                .mutateAsync({
                    bookmark: content,
                    timeCode,
                    duration,
                    gamification: {
                        ...gamificationFields,
                        isGamificationEnabled: true,
                    },
                })
                .catch(() => undefined);
        },
        [dispatch, addBookmarkMutation, config]
    );
    const { mutate: removeBookmark } = useDeleteBookmarkMutation();
    const deleted = useRef(false);
    const lastBookmarkTs = useRef(0);

    const onTimeChanged = useCallback(
        (event: any) => {
            const bookmarkSettings = h.getBookmarkSettings();
            const bookmarkingRemovalPercentage = bookmarkSettings.removalPercentage;
            const bookmarkWithDelete = bookmarkSettings.withDelete;
            if (!selected || !('startTime' in selected) || !('endTime' in selected)) {
                return;
            }
            if (!playerService?.player || !isPlayerAlive(playerService.player)) return;

            const now = Date.now();
            if (now - lastBookmarkTs.current < bookmarksInterval * 1000) return;

            lastBookmarkTs.current = now;
            if (playerService?.player?.isPaused()) return;

            let duration = 0;
            let timeCode = 0;

            if (playerService?.player.isLive() && selected.startTime && selected.endTime) {
                duration = Math.ceil((Number(selected.endTime) - Number(selected.startTime)) / 1000);
                timeCode = Math.floor(event.time - Number(selected.startTime) / 1000);
            } else {
                timeCode = Math.ceil(event.time);
                duration = Math.ceil(playerService?.player?.getDuration());
            }

            const maximumDuration = (duration * bookmarkingRemovalPercentage) / 100;
            if (!playerService?.player?.isPaused() && timeCode > 0 && timeCode < maximumDuration) {
                addBookmark(selected, timeCode, duration);
            }
            if (
                bookmarkWithDelete &&
                !playerService?.player?.isPaused() &&
                !playerService?.player?.isLive() &&
                timeCode > 0 &&
                timeCode >= maximumDuration &&
                !deleted.current
            ) {
                removeBookmark({ bookmark: selected });
                deleted.current = true;
            }
        },
        [selected, bookmarksInterval, addBookmark, removeBookmark]
    );

    useEffect(() => {
        if (!selected || !('startTime' in selected) || !('endTime' in selected)) {
            return;
        }
        if (!playerService?.player) return;

        playerService?.player?.on(PlayerEvent.TimeChanged, onTimeChanged);

        return () => {
            safePlayerOff(playerService?.player, PlayerEvent.TimeChanged, onTimeChanged);
        };
    }, [selected, onTimeChanged]);

    const stopBookmark = () => {
        // Remove the event listener by passing both event and handler
        safePlayerOff(playerService?.player, PlayerEvent.TimeChanged, onTimeChanged);
    };

    const isBookmarked = async (id: string) => {
        const [err, response] = await to(apis.getJawwyService().bookmarksService().get());
        if (response) {
            return (response as any[]).find((item: any) => item.assetid === id);
        }
        return;
    };

    return {
        setSelected,
        isBookmarked,
        stopBookmark,
        addBookmark,
        removeBookmark,
        isLoading,
        bookmarks,
    };
};

export default useBookmark;
